import { maybe } from '@passionware/monads';
import { pickBy } from 'lodash';
import {
  bookingRateIntervalToWireEnum,
  expenseRateIntervalToWireEnum
} from '../resourcingInterval.http';
import type {
  BudgetExpenseLineCreateInput,
  BudgetExpenseSlotCreateTemplate,
  ResourceSlotCreateTemplate
} from 'v5/core/production-resourcing/mutation-inputs';
import { isClientOnlyExpenseLineId } from 'v5/core/expense-billing/bookingExpenseLineMutation';
import type {
  BookingExpensesUpdate,
  BookingFeesUpdate,
  BookingRateAndFeesUpdate,
  BookingRateUpdate,
  BookingStatusUpdate,
  BudgetExpenseMetadataUpdate,
  BudgetExpensePlanUpdate,
  BudgetExpenseUpdate,
  ExpenseLineCommittedUpdate,
  ResourceSlotMetadataUpdate,
  ResourceSlotPlanUpdate,
  ResourceSlotUpdate
} from 'v5/core/production-resourcing/mutation-patches';
import { isDefined } from 'v5/platform/lang/isDefined';
import type {
  BookingExpensesUpdatePatch,
  BookingFeesPatch,
  BookingRateAndFeesPatch,
  BookingRatePatch,
  BookingStatusPatch,
  BudgetExpensePlanPatch,
  BudgetExpenseUpdatePatch,
  ExpenseLineUpdatePatch,
  ResourceSlotMetadataPatch,
  ResourceSlotPlanFeesPatch,
  ResourceSlotUpdatePatch
} from '../resourcing.mutation.types';

function feesToWireEstimateFees(
  fees: NonNullable<ResourceSlotPlanUpdate['rate']>['fees']
): Partial<ResourceSlotPlanFeesPatch> {
  if (!maybe.isPresent(fees)) {
    return {};
  }
  const f = maybe.getOrThrow(fees);
  return {
    estimate_rate_agency_fee: f.agency,
    estimate_rate_payroll_fee: f.payroll,
    estimate_rate_processing_fee: f.processing
  };
}

function metadataFieldsFromSlotUpdate(
  update: ResourceSlotUpdate | BudgetExpenseUpdate
): ResourceSlotMetadataUpdate {
  return pickBy(
    {
      note: 'note' in update ? update.note : undefined,
      includeInTotals:
        'includeInTotals' in update ? update.includeInTotals : undefined,
      slotCategoryId:
        'slotCategoryId' in update ? update.slotCategoryId : undefined
    },
    isDefined
  );
}

function planFieldsFromSlotUpdate(
  update: ResourceSlotUpdate
): ResourceSlotPlanUpdate {
  return pickBy(
    {
      estimateQuantity:
        'estimateQuantity' in update ? update.estimateQuantity : undefined,
      rate: 'rate' in update ? update.rate : undefined
    },
    isDefined
  );
}

function planFieldsFromBudgetExpenseUpdate(
  update: BudgetExpenseUpdate
): BudgetExpensePlanUpdate {
  return pickBy(
    {
      estimateQuantity:
        'estimateQuantity' in update ? update.estimateQuantity : undefined,
      rate: 'rate' in update ? update.rate : undefined
    },
    isDefined
  );
}

export function resourceSlotPlanUpdateToWire(
  update: ResourceSlotPlanUpdate
): ResourceSlotUpdatePatch {
  const rate = update.rate;

  return pickBy(
    {
      estimate_rate_quantity: update.estimateQuantity,
      estimate_rate_amount: rate?.amountPerUnit,
      estimate_rate_currency: rate?.currency,
      estimate_rate_interval:
        rate?.interval !== undefined
          ? bookingRateIntervalToWireEnum(rate.interval)
          : undefined,
      ...(rate?.fees !== undefined ? feesToWireEstimateFees(rate.fees) : {})
    },
    isDefined
  );
}

export function resourceSlotMetadataUpdateToWire(
  update: ResourceSlotMetadataUpdate
): ResourceSlotMetadataPatch {
  return pickBy(
    {
      note: update.note,
      include_in_calculation: update.includeInTotals,
      slot_category_id: update.slotCategoryId
    },
    isDefined
  );
}

export function resourceSlotUpdateToWire(
  update: ResourceSlotUpdate
): ResourceSlotUpdatePatch {
  return {
    ...resourceSlotMetadataUpdateToWire(metadataFieldsFromSlotUpdate(update)),
    ...resourceSlotPlanUpdateToWire(planFieldsFromSlotUpdate(update))
  };
}

function budgetExpensePlanUpdateToWire(
  update: BudgetExpensePlanUpdate
): BudgetExpensePlanPatch {
  const rate = update.rate;

  return pickBy(
    {
      estimate_rate_quantity: update.estimateQuantity,
      estimate_rate_amount: rate?.amountPerUnit,
      estimate_rate_currency: rate?.currency,
      estimate_rate_interval:
        rate?.interval !== undefined
          ? expenseRateIntervalToWireEnum(rate.interval)
          : undefined
    },
    isDefined
  );
}

export function budgetExpenseUpdateToWire(
  update: BudgetExpenseUpdate
): BudgetExpenseUpdatePatch {
  return {
    ...resourceSlotMetadataUpdateToWire(metadataFieldsFromSlotUpdate(update)),
    ...budgetExpensePlanUpdateToWire(planFieldsFromBudgetExpenseUpdate(update))
  };
}

export function budgetExpenseSlotCreateTemplateToWire(
  template: Partial<BudgetExpenseSlotCreateTemplate>
): BudgetExpenseUpdatePatch {
  return budgetExpenseUpdateToWire(
    pickBy(template, isDefined) as BudgetExpenseMetadataUpdate &
      BudgetExpensePlanUpdate
  );
}

export function resourceSlotCreateTemplateToWire(
  template: Partial<ResourceSlotCreateTemplate>
): ResourceSlotUpdatePatch {
  return resourceSlotUpdateToWire(
    pickBy(template, isDefined) as ResourceSlotMetadataUpdate &
      ResourceSlotPlanUpdate
  );
}

export function budgetExpenseLineCreateInputToWire(
  input: BudgetExpenseLineCreateInput
): {
  production_budget_expense_id: number;
  slot_category_id: number;
  booking_id: null;
  commit_vendor: string;
  commit_rate_quantity?: number;
  commit_rate_amount?: number;
  commit_rate_currency?: string;
  commit_rate_interval?: string;
} {
  return {
    production_budget_expense_id: input.productionBudgetExpenseId,
    slot_category_id: input.slotCategoryId,
    booking_id: null,
    commit_vendor: input.vendor,
    ...expenseLineCommittedUpdateToWire({
      vendor: input.vendor,
      charge: input.charge
    })
  };
}

function bookingRateAndFeesUpdateToWire(
  update: BookingRateAndFeesUpdate
): BookingRatePatch | BookingFeesPatch | BookingRateAndFeesPatch {
  return pickBy(
    {
      rate_amount: update.amountPerUnit,
      rate_currency: update.currency,
      rate_interval:
        update.interval !== undefined
          ? bookingRateIntervalToWireEnum(update.interval)
          : undefined,
      rate_agency_fee: update.agency,
      rate_payroll_fee: update.payroll,
      rate_processing_fee: update.processing
    },
    isDefined
  );
}

export function bookingResourcingUpdateToWire(
  patch:
    | BookingRateUpdate
    | BookingFeesUpdate
    | BookingRateAndFeesUpdate
    | BookingStatusUpdate
):
  | BookingRatePatch
  | BookingFeesPatch
  | BookingRateAndFeesPatch
  | BookingStatusPatch {
  if ('statusId' in patch) {
    return bookingStatusUpdateToWire(patch);
  }
  return bookingRateAndFeesUpdateToWire(patch);
}

export function bookingStatusUpdateToWire(
  update: BookingStatusUpdate
): BookingStatusPatch {
  return pickBy({ status_id: update.statusId }, isDefined);
}

export function expenseLineCommittedUpdateToWire(
  update: ExpenseLineCommittedUpdate
): ExpenseLineUpdatePatch {
  const charge = update.charge;

  return pickBy(
    {
      commit_vendor: update.vendor,
      commit_rate_quantity: charge?.quantity,
      commit_rate_amount: charge?.amountPerUnit,
      commit_rate_currency: charge?.currency,
      commit_rate_interval:
        charge?.interval !== undefined
          ? expenseRateIntervalToWireEnum(charge.interval)
          : undefined
    },
    isDefined
  );
}

function bookingExpenseLineUpdateToWire(
  line: BookingExpensesUpdate['expenses'][number]
) {
  const persistedId = isClientOnlyExpenseLineId(line.id)
    ? undefined
    : Number(line.id);

  return pickBy(
    {
      id: persistedId,
      slot_category_id: line.slotCategoryId ?? undefined,
      commit_vendor: line.vendor,
      commit_rate_amount: line.charge.amountPerUnit,
      commit_rate_quantity: line.charge.quantity,
      commit_rate_currency: line.charge.currency,
      commit_rate_interval: expenseRateIntervalToWireEnum(line.charge.interval),
      archived: line.archived
    },
    isDefined
  );
}

export function bookingExpensesUpdateToWire(
  update: BookingExpensesUpdate
): BookingExpensesUpdatePatch {
  return {
    expenses: update.expenses.map(bookingExpenseLineUpdateToWire)
  };
}
